/**
 * Entity span resolution for `AnalystMarkdown`.
 *
 * Spans arrive as offsets into the raw markdown **source**, produced by the
 * model or the enrichment service, and are resolved against the syntax tree.
 * They are never found by pattern-matching the rendered prose: a pattern that
 * finds IP addresses also finds `1.2.3.4` in "upgrade to version 1.2.3.4", a
 * hash pattern matches any long hex string including a commit SHA nobody can
 * pivot on, and every false positive attaches an action menu to something with
 * no entity behind it. Analyst trust does not survive the first one.
 *
 * Resolving against the source rather than the rendered text is what keeps the
 * offsets stable: every emphasis marker, link and escape would otherwise shift
 * the indices and silently attach entities to the wrong words.
 */
import type { EntityKind, EntityVerdict } from '@/atoms/Chip';

/** The custom element the transform emits, mapped to a Chip when rendering. */
export const ENTITY_ELEMENT = 'octopus-entity';

export interface EntitySpan {
  /** Start offset into the raw markdown source, inclusive. */
  start: number;
  /** End offset into the raw markdown source, exclusive. */
  end: number;
  kind: EntityKind;
  /** Canonical full value. What a copy action yields. */
  value: string;
  /** Shortened text to display. Defaults to the marked source text. */
  display?: string;
  /** Omit when the value was never resolved. */
  verdict?: EntityVerdict;
  /**
   * Enrichment facts shown in the entity menu. Kept off the rendered element
   * and looked up by canonical value at open time, so nothing structural ends
   * up serialised into DOM attributes.
   */
  attributes?: { label: string; value: string }[];
}

/** Minimal structural view of mdast, so this module needs no tree typings. */
interface MdNode {
  type: string;
  value?: string;
  children?: MdNode[];
  position?: { start: { offset?: number }; end: { offset?: number } };
  data?: Record<string, unknown>;
}

/**
 * Node types whose text must never be marked.
 *
 * Code is copied verbatim as a unit and an interactive element inside it
 * corrupts that. Headings and links already own their click behaviour, so a
 * second target inside them is ambiguous. The rest carry no prose.
 */
const PROTECTED_TYPES: ReadonlySet<string> = new Set([
  'code',
  'inlineCode',
  'heading',
  'link',
  'linkReference',
  'image',
  'imageReference',
  'definition',
  'footnoteDefinition',
  'html',
  'yaml',
  'toml',
]);

export interface ApplyEntitySpansOptions {
  /**
   * Cap on marked spans per top-level block. A summary genuinely dense with
   * indicators wants a table underneath it, not more chips inside it.
   */
  maxPerBlock?: number;
  /** Reports spans that could not be applied. Defaults to a dev-only warning. */
  onSkipped?: (span: EntitySpan, reason: string) => void;
}

const DEFAULT_MAX_PER_BLOCK = 8;

function warnSkipped(span: EntitySpan, reason: string): void {
  if (process.env.NODE_ENV === 'production') return;
  console.warn(
    `[Octopus UI] AnalystMarkdown skipped the entity span for "${span.value}" ` +
      `at ${span.start}-${span.end}: ${reason}`,
  );
}

interface BlockState {
  /** Values already marked in this block, so later mentions stay plain text. */
  seen: Set<string>;
  remaining: number;
}

/**
 * Rewrites text nodes in place so each applicable span becomes an entity node.
 *
 * Mutates the tree, which is what a remark transformer is expected to do.
 */
export function applyEntitySpans(
  tree: MdNode,
  spans: readonly EntitySpan[],
  options: ApplyEntitySpansOptions = {},
): void {
  if (spans.length === 0) return;

  const maxPerBlock = options.maxPerBlock ?? DEFAULT_MAX_PER_BLOCK;
  const ordered = [...spans].sort((a, b) => a.start - b.start || b.end - a.end);

  // A span that crosses a node boundary is examined once per text node it
  // touches, so reporting is deduplicated here rather than at each call site --
  // otherwise one bad span from the resolver looks like three.
  const report = options.onSkipped ?? warnSkipped;
  const reported = new Set<EntitySpan>();
  const onSkipped = (span: EntitySpan, reason: string) => {
    if (reported.has(span)) return;
    reported.add(span);
    report(span, reason);
  };

  // Per top-level block, so the first-mention rule and the density cap are
  // scoped to a paragraph rather than to the whole document.
  for (const block of tree.children ?? []) {
    visit(block, false, ordered, { seen: new Set(), remaining: maxPerBlock }, onSkipped);
  }
}

function visit(
  node: MdNode,
  isProtected: boolean,
  spans: readonly EntitySpan[],
  state: BlockState,
  onSkipped: NonNullable<ApplyEntitySpansOptions['onSkipped']>,
): void {
  const protectedHere = isProtected || PROTECTED_TYPES.has(node.type);
  const children = node.children;
  if (!children || children.length === 0) return;

  // A GFM table's first row is its header, which labels columns rather than
  // carrying prose.
  const headerRowIndex = node.type === 'table' ? 0 : -1;

  const next: MdNode[] = [];
  children.forEach((child, index) => {
    const childProtected = protectedHere || index === headerRowIndex;

    if (child.type === 'text' && !childProtected) {
      next.push(...splitTextNode(child, spans, state, onSkipped));
      return;
    }

    visit(child, childProtected, spans, state, onSkipped);
    next.push(child);
  });

  node.children = next;
}

/**
 * Replaces one text node with an alternating run of text and entity nodes.
 * Returns the node untouched when nothing applies.
 */
function splitTextNode(
  node: MdNode,
  spans: readonly EntitySpan[],
  state: BlockState,
  onSkipped: NonNullable<ApplyEntitySpansOptions['onSkipped']>,
): MdNode[] {
  const nodeStart = node.position?.start.offset;
  const nodeEnd = node.position?.end.offset;
  const text = node.value ?? '';

  if (nodeStart === undefined || nodeEnd === undefined) return [node];

  // If the node's text is not the same length as the source range it claims,
  // character references or escapes were resolved during parsing and relative
  // offsets no longer line up. Marking anyway would shift every span in this
  // node onto neighbouring words, so the node is left alone.
  if (text.length !== nodeEnd - nodeStart) {
    for (const span of spans) {
      if (span.start >= nodeStart && span.end <= nodeEnd) {
        onSkipped(span, 'the surrounding text node was rewritten during parsing');
      }
    }
    return [node];
  }

  const out: MdNode[] = [];
  let cursor = nodeStart;

  for (const span of spans) {
    // Spans are sorted by start, so nothing later can touch this node.
    if (span.start >= nodeEnd) break;
    // Belongs to an earlier node, which has already dealt with it.
    if (span.end <= nodeStart) continue;

    if (span.start < nodeStart || span.end > nodeEnd) {
      onSkipped(span, 'it crosses a markdown node boundary');
      continue;
    }
    if (span.start < cursor) {
      onSkipped(span, 'it overlaps an earlier span');
      continue;
    }
    if (state.remaining <= 0) {
      onSkipped(span, 'the block already reached its marked-span limit');
      continue;
    }
    // Only the first mention in a block is marked: the chip already opens the
    // same menu, and a paragraph where every repeat is a chip is unreadable.
    if (state.seen.has(span.value)) continue;

    if (span.start > cursor) {
      out.push(textNode(text.slice(cursor - nodeStart, span.start - nodeStart)));
    }
    out.push(entityNode(span, text.slice(span.start - nodeStart, span.end - nodeStart)));

    state.seen.add(span.value);
    state.remaining -= 1;
    cursor = span.end;
  }

  if (out.length === 0) return [node];
  if (cursor < nodeEnd) out.push(textNode(text.slice(cursor - nodeStart)));
  return out;
}

function textNode(value: string): MdNode {
  return { type: 'text', value };
}

/**
 * Emits a node that mdast-util-to-hast turns into `<octopus-entity>` carrying
 * `data-*` attributes. Data attributes rather than bespoke props so nothing
 * reaches the DOM that React would warn about if the mapping were ever missing.
 */
function entityNode(span: EntitySpan, sourceText: string): MdNode {
  const hProperties: Record<string, string> = {
    'data-entity-kind': span.kind,
    'data-entity-value': span.value,
    'data-entity-display': span.display ?? sourceText,
  };
  if (span.verdict) hProperties['data-entity-verdict'] = span.verdict;

  return {
    type: 'octopusEntity',
    children: [textNode(span.display ?? sourceText)],
    data: { hName: ENTITY_ELEMENT, hProperties },
  };
}

/**
 * remark plugin form, for `ReactMarkdown`'s `remarkPlugins`.
 *
 * @example remarkPlugins={[remarkGfm, [remarkEntitySpans, { spans }]]}
 */
export function remarkEntitySpans(
  options: ApplyEntitySpansOptions & { spans?: readonly EntitySpan[] } = {},
) {
  const { spans = [], ...rest } = options;
  return (tree: MdNode) => applyEntitySpans(tree, spans, rest);
}
